import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { Badge, Box, Button, Checkbox, Divider, IconButton, List, ListItemButton, ListItemText, Popover, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "../components";
import { formatRelative } from "../i18n/format";
import help from "./HELP.md?raw";
import { useMarkRead, useNotifications, usePreferences, useSavePreferences, useUnreadCount, type Notification } from "./useData";

function Row({ n, onOpen }: { n: Notification; onOpen: (n: Notification) => void }) {
  return (
    <ListItemButton onClick={() => onOpen(n)} sx={{ alignItems: "flex-start", bgcolor: n.read_at ? "transparent" : "action.hover" }}>
      <ListItemText primary={<Typography variant="body2" fontWeight={n.read_at ? 400 : 600} color={n.importance === "critical" ? "error" : "text.primary"}>{n.title}</Typography>}
        secondary={<>{n.body && <span>{n.body.slice(0, 120)} · </span>}<span>{formatRelative(n.created_at)}</span></>} />
    </ListItemButton>
  );
}

/** Bell in the app bar (CF-0111): unread badge, recent list, mark all read. */
export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const unread = useUnreadCount();
  const list = useNotifications(false);
  const mark = useMarkRead();
  const navigate = useNavigate();
  const open = (n: Notification) => { if (!n.read_at) mark.mutate(n.id); setAnchor(null); if (n.url) navigate(n.url); };
  return (
    <>
      <Tooltip title="Notifications"><IconButton aria-label="Notifications" onClick={(e) => setAnchor(e.currentTarget)}><Badge badgeContent={unread.data?.count ?? 0} color="error"><NotificationsNoneIcon /></Badge></IconButton></Tooltip>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
        <Box sx={{ width: 380 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle2">Notifications</Typography>
            <Button size="small" onClick={() => mark.mutate("all")} disabled={!unread.data?.count}>Mark all read</Button>
          </Stack>
          <Divider />
          <List dense sx={{ maxHeight: 420, overflow: "auto" }}>
            {list.data?.results.length === 0 && <Typography sx={{ p: 2 }} color="text.secondary">You're all caught up.</Typography>}
            {list.data?.results.slice(0, 15).map((n) => <Row key={n.id} n={n} onOpen={open} />)}
          </List>
          <Divider />
          <Button fullWidth onClick={() => { setAnchor(null); navigate("/app/notifications"); }}>See all</Button>
        </Box>
      </Popover>
    </>
  );
}

export function NotificationsPage() {
  const list = useNotifications(false);
  const mark = useMarkRead();
  const navigate = useNavigate();
  return (
    <>
      <PageHeader help={help} title="Notifications" actions={<Button onClick={() => mark.mutate("all")}>Mark all read</Button>} />
      <List sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider" }}>
        {list.data?.results.map((n) => <Row key={n.id} n={n} onOpen={(x) => { if (!x.read_at) mark.mutate(x.id); if (x.url) navigate(x.url); }} />)}
        {list.data?.results.length === 0 && <Typography sx={{ p: 3 }} color="text.secondary">Nothing yet.</Typography>}
      </List>
    </>
  );
}

/** Embedded in the profile page (CF-0112). */
export function NotificationPreferences() {
  const prefs = usePreferences();
  const save = useSavePreferences();
  const toggle = (p: { kind: string }, field: "in_app" | "email" | "digest", value: boolean) => save.mutate([{ kind: p.kind, [field]: value }]);
  const groups = new Map<string, NonNullable<typeof prefs.data>>();
  prefs.data?.forEach((p) => groups.set(p.group, [...(groups.get(p.group) ?? []), p]));
  return (
    <Table size="small">
      <TableHead><TableRow><TableCell>Notification</TableCell><TableCell align="center">In app</TableCell><TableCell align="center">Email</TableCell><TableCell align="center">Daily digest</TableCell></TableRow></TableHead>
      <TableBody>
        {[...groups.entries()].map(([group, items]) => (
          <>
            <TableRow key={`g-${group}`}><TableCell colSpan={4} sx={{ bgcolor: "action.hover", textTransform: "capitalize", fontWeight: 600 }}>{group}</TableCell></TableRow>
            {items.map((p) => (
              <TableRow key={p.kind}>
                <TableCell>{p.label}{p.critical && <Typography variant="caption" color="text.secondary"> (always on)</Typography>}</TableCell>
                <TableCell align="center"><Checkbox size="small" checked={p.in_app} disabled={p.critical} onChange={(e) => toggle(p, "in_app", e.target.checked)} /></TableCell>
                <TableCell align="center"><Checkbox size="small" checked={p.email} disabled={p.critical} onChange={(e) => toggle(p, "email", e.target.checked)} /></TableCell>
                <TableCell align="center"><Checkbox size="small" checked={p.digest} disabled={p.critical} onChange={(e) => toggle(p, "digest", e.target.checked)} /></TableCell>
              </TableRow>
            ))}
          </>
        ))}
      </TableBody>
    </Table>
  );
}
